'use client'

import type { MuxPlayerRefAttributes } from '@mux/mux-player-react'
import type { RefObject } from 'react'
import { useCallback, useEffect, useRef } from 'react'

const PROGRESS_REPORT_INTERVAL_MS = 5_000

export interface ProgressInfo {
  positionSeconds: number
  durationSeconds: number
  percent: number
  forceComplete?: boolean
}

/**
 * Throttled watch-progress reporting for the lesson player.
 *
 * Reads position/duration off the player element, throttles periodic
 * `timeupdate` reports to one per PROGRESS_REPORT_INTERVAL_MS, force-flushes
 * on pause/unmount/pagehide, and treats the `ended` event as the
 * authoritative "watched" signal (players sometimes report currentTime a hair
 * below duration when it fires, so `forceComplete` lets the server mark the
 * lesson done regardless).
 */
export function useProgressReporting(
  playerRef: RefObject<MuxPlayerRefAttributes | null>,
  onProgress?: (info: ProgressInfo) => void,
  onEnded?: () => void,
) {
  const lastReportRef = useRef<number>(0)
  const onProgressRef = useRef(onProgress)
  useEffect(() => {
    onProgressRef.current = onProgress
  }, [onProgress])

  const reportProgress = useCallback((force: boolean) => {
    const cb = onProgressRef.current
    if (!cb)
      return
    const player = playerRef.current
    if (!player)
      return
    const duration = player.duration
    const position = player.currentTime
    if (!Number.isFinite(duration) || duration <= 0)
      return
    if (!Number.isFinite(position) || position < 0)
      return
    const now = Date.now()
    if (!force && now - lastReportRef.current < PROGRESS_REPORT_INTERVAL_MS) {
      return
    }
    lastReportRef.current = now
    const percent = Math.max(0, Math.min(100, Math.round((position / duration) * 100)))
    cb({
      positionSeconds: position,
      durationSeconds: duration,
      percent,
    })
  }, [playerRef])

  const handleTimeUpdate = useCallback(() => {
    reportProgress(false)
  }, [reportProgress])

  const handlePause = useCallback(() => {
    reportProgress(true)
  }, [reportProgress])

  const handleEnded = useCallback(() => {
    const cb = onProgressRef.current
    const player = playerRef.current
    if (cb) {
      const duration = Number.isFinite(player?.duration) && (player?.duration ?? 0) > 0
        ? (player!.duration)
        : 1
      lastReportRef.current = Date.now()
      cb({
        positionSeconds: duration,
        durationSeconds: duration,
        percent: 100,
        forceComplete: true,
      })
    }
    onEnded?.()
  }, [onEnded, playerRef])

  // Flush a final progress report when the user navigates away mid-video.
  useEffect(() => {
    const flush = () => reportProgress(true)
    window.addEventListener('pagehide', flush)
    return () => {
      window.removeEventListener('pagehide', flush)
      // Also flush on unmount (e.g. moving to next lesson).
      flush()
    }
  }, [reportProgress])

  return { handleTimeUpdate, handlePause, handleEnded }
}
